import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail, Instagram, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_YELLOW_URL = "https://game-invoice.preview.emergentagent.com/api/files/official-whistle/branding/67ca9f95-5248-441e-9eb3-5fc8cf5c72fb/47eefe68-1dae-481a-a902-450e03018adb.png";

export const TeamPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/team`).then(res => {
      setMembers(res.data);
    }).catch(err => console.error('Failed to load team', err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-black/90 backdrop-blur-xl border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img src={LOGO_YELLOW_URL} alt="Logo" fetchpriority="high" decoding="async" loading="eager" className="w-9 h-9 sm:w-10 sm:h-10" />
            <div className="hidden sm:block">
              <p className="font-heading font-bold text-xs sm:text-sm uppercase tracking-wider leading-none">Official</p>
              <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] sm:tracking-[0.3em] text-amber-500 leading-none mt-1">With The Whistle</p>
            </div>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-zinc-300 hover:text-white" data-testid="team-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Home
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-24 md:pt-28 pb-12 md:pb-20 bg-gradient-to-b from-zinc-950 to-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-amber-400 mb-4">
            Our People
          </div>
          <h1 className="font-heading font-black uppercase tracking-tight leading-[0.95]" style={{ fontSize: 'clamp(2.5rem, 7vw, 5rem)' }}>
            Meet The <span className="text-amber-500">Team</span>
          </h1>
          <p className="mt-4 md:mt-5 text-base md:text-lg text-zinc-400 max-w-2xl mx-auto">
            The certified officials, leaders, and mentors behind Official With The Whistle.
          </p>
        </div>
      </section>

      {/* Members grid */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="animate-spin w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full" /></div>
          ) : members.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Users className="w-12 h-12 text-zinc-700 mb-4" />
              <p className="text-lg text-zinc-400">Team members coming soon.</p>
              <p className="text-sm text-zinc-600 mt-2">Check back shortly.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
              {members.map((m, idx) => (
                <button
                  key={m.id}
                  onClick={() => setSelected(m)}
                  data-testid={`team-member-${idx}`}
                  className="group text-left bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-amber-500/60 transition-all hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <div className="aspect-[3/4] bg-gradient-to-br from-zinc-800 to-zinc-900 relative overflow-hidden">
                    {m.photo_url ? (
                      <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Users className="w-16 h-16 text-zinc-700" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                      <h3 className="font-heading font-bold text-lg md:text-xl uppercase leading-tight text-white">{m.name}</h3>
                      <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mt-1">{m.role}</p>
                    </div>
                  </div>
                  {m.bio && (
                    <div className="p-4 md:p-5">
                      <p className="text-sm text-zinc-400 line-clamp-3">{m.bio}</p>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-amber-500 mt-3 group-hover:text-amber-400">Read More →</p>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bio Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <div className="max-w-3xl w-full bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden my-8" onClick={e => e.stopPropagation()}>
            <div className="grid md:grid-cols-2">
              <div className="aspect-[3/4] md:aspect-auto bg-zinc-950 relative">
                {selected.photo_url ? (
                  <img src={selected.photo_url} alt={selected.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Users className="w-20 h-20 text-zinc-700" /></div>
                )}
              </div>
              <div className="p-6 md:p-8">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-amber-500 mb-2">{selected.role}</p>
                <h2 className="font-heading font-black uppercase leading-tight text-2xl md:text-4xl mb-4">{selected.name}</h2>
                <div className="text-sm md:text-base text-zinc-300 leading-relaxed whitespace-pre-wrap">{selected.bio || 'No bio available.'}</div>
                <Button onClick={() => setSelected(null)} className="mt-6 w-full bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="close-bio-btn">Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
